import { TemplateStepBindingStatus } from "./template-step-binding-status";
import { groupStepVariables } from "@/domains/templates/step-variable-bindings";
import { useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronUp,
  Copy,
  Loader2,
  Pencil,
  Trash2,
} from "lucide-react";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import type { TemplateInputFieldEditorData } from "@/domains/templates/components/template-input-fields/template-input-field-editor-row";
import { formatOutputTypeLabel } from "@/domains/templates/output-types";
import type { UiTemplateSection } from "@/features/template-builder/queries";
import { useParamOrThrow } from "@/shared/hooks/use-param-or-throw";
import { cn } from "@/shared/lib/utils";
import { getErrorMessage } from "@/shared/lib/error-message";
import { useDeleteStep } from "../hooks/use-delete-step";
import { useInsertStepAtPosition } from "../hooks/use-insert-step-at-position";
import { useMoveStep } from "../hooks/use-move-step";
import { useTemplateStepOpenState } from "../hooks/use-template-step-open-state";
import { useUpdateTemplateStep } from "../hooks/use-update-template-step";
import { TemplateStepMetadataDialog } from "./template-step-metadata-dialog";
import { TemplateMarkdown } from "./template-markdown";
import {
  TemplateStepCommandDialog,
  type TemplateStepCommand,
} from "./template-step-command-dialog";
import { TemplateVariableList } from "./template-variable-list";

export type TemplateStepProps = {
  section: UiTemplateSection;
  isReadOnly?: boolean;
  variableEditorData: TemplateInputFieldEditorData;
};

export function TemplateStep({
  section,
  isReadOnly = false,
  variableEditorData,
}: TemplateStepProps) {
  const { id, name, order, description, outputTypes, variables } = section;
  const structurallyIncomplete = variables.some(
    (variable) =>
      variable.binding_kind === "unbound" ||
      (variable.variable_role === "subanalysis_source" &&
        (!variable.binding_source_step_id ||
          !variable.binding_source_variable_id ||
          !!variable.binding_path)) ||
      (variable.binding_kind === "step_variable" &&
        (!variable.binding_source_variable_id || !!variable.binding_path)),
  );
  const templateId = useParamOrThrow("uuid");
  const updateStep = useUpdateTemplateStep(templateId);
  const moveStep = useMoveStep(templateId);
  const duplicateStep = useInsertStepAtPosition();
  const deleteStep = useDeleteStep(templateId);
  const [open, setOpen] = useTemplateStepOpenState(id);
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [command, setCommand] = useState<TemplateStepCommand>();
  const [moveError, setMoveError] = useState<string>();
  const isCommandPending =
    moveStep.isPending || duplicateStep.isPending || deleteStep.isPending;

  const runCommand = (): Promise<unknown> => {
    switch (command) {
      case "duplicate":
        return duplicateStep.mutateAsync({
          templateId,
          position: order + 1,
          templateStepId: id,
        });
      case "delete":
        return deleteStep.mutateAsync(id);
      default:
        return Promise.reject(new Error("Unsupported section command"));
    }
  };

  const handleMove = async (direction: "up" | "down") => {
    setMoveError(undefined);
    try {
      await moveStep.mutateAsync({ id, direction });
    } catch (error) {
      setMoveError(getErrorMessage(error, "Could not move section"));
    }
  };

  return (
    <Card
      id={`template-step-${id}`}
      className={cn(
        "scroll-mt-20 gap-0 py-0",
        isReadOnly ? "border-muted bg-muted/30 opacity-80" : undefined,
      )}
    >
      <CardHeader className="min-h-12 items-center px-3 py-2">
        <div className="flex min-w-0 items-center gap-1">
          {!isReadOnly && (
            <>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => void handleMove("up")}
                disabled={isCommandPending}
                aria-label="Move up"
              >
                {moveStep.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowUp className="h-4 w-4" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => void handleMove("down")}
                disabled={isCommandPending}
                aria-label="Move down"
              >
                {moveStep.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ArrowDown className="h-4 w-4" />
                )}
              </Button>
            </>
          )}
          <CardTitle className="ml-1 flex min-w-0 flex-wrap items-center gap-1.5 text-base leading-tight">
            <span className="truncate">{name}</span>
            {section.subanalysis.kind !== "ordinary" && (
              <Badge variant="secondary">Execute subanalysis</Badge>
            )}
            {section.subanalysis.kind === "ordinary" &&
              outputTypes.map((outputType) => (
                <Badge key={outputType} variant="secondary" className="text-xs">
                  {formatOutputTypeLabel(outputType)}
                </Badge>
              ))}
            {structurallyIncomplete ? (
              <Badge variant="outline">Incomplete</Badge>
            ) : variables.some(
                (variable) =>
                  variable.binding_kind === "step_variable" ||
                  variable.binding_kind === "step_output",
              ) ? (
              <TemplateStepBindingStatus
                templateId={templateId}
                section={section}
              />
            ) : null}
            {isReadOnly && <Badge variant="outline">Read-only</Badge>}
          </CardTitle>
        </div>
        <CardAction>
          <div className="flex items-center gap-1">
            {!isReadOnly && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Edit section"
                  title="Edit section"
                  onClick={() => setMetadataOpen(true)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Duplicate section"
                  title="Duplicate section"
                  onClick={() => setCommand("duplicate")}
                  disabled={isCommandPending}
                >
                  <Copy className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Delete section"
                  title="Delete section"
                  onClick={() => setCommand("delete")}
                  disabled={isCommandPending}
                  className="text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => setOpen((value) => !value)}
              aria-label={open ? "Collapse" : "Expand"}
            >
              {open ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>
          </div>
        </CardAction>
      </CardHeader>
      {moveError ? (
        <p role="alert" className="border-t px-3 py-2 text-sm text-destructive">
          {moveError}
        </p>
      ) : null}
      {open && (
        <CardContent className="flex flex-col gap-3 border-t px-3 py-3">
          <div className="rounded-md border p-3">
            <p className="text-sm font-medium">Section description</p>
            <TemplateMarkdown
              content={description ?? ""}
              emptyText="No description"
              className="text-sm"
            />
          </div>
          {section.subanalysis.kind === "invalid" ||
          section.subanalysis.kind === "unsupported" ? (
            <p role="alert">{section.subanalysis.message}</p>
          ) : null}
          {(["input", "output"] as const).map((group) => (
            <div key={group} className="space-y-2">
              <p className="text-sm font-medium">
                {group === "input" ? "Input variables" : "Output variables"}
              </p>
              <TemplateVariableList
                target={{ level: "analysisstep", templateId, stepId: id }}
                variables={
                  groupStepVariables(variables)[
                    group === "input" ? "inputs" : "outputs"
                  ]
                }
                group={group}
                editorData={variableEditorData}
                isReadOnly={isReadOnly}
                emptyText={`No ${group} variables configured.`}
              />
            </div>
          ))}
        </CardContent>
      )}
      <TemplateStepMetadataDialog
        open={metadataOpen}
        section={section}
        isPending={updateStep.isPending}
        onOpenChange={setMetadataOpen}
        onSave={(values) =>
          updateStep.mutateAsync({
            id,
            name: values.name,
            description: values.description || null,
            outputTypes: values.outputTypes,
          })
        }
      />
      <TemplateStepCommandDialog
        open={Boolean(command)}
        command={command ?? "duplicate"}
        sectionName={name}
        isPending={isCommandPending}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setCommand(undefined);
        }}
        onConfirm={runCommand}
      />
    </Card>
  );
}
